<template>
    <AdminLayout>
        <template #header>
            <Header :menu="main_menu" v-if="$page.props.auth.user" />
        </template>
        <template #default>
            <div class="mt-10 space-y-6">
                <!-- Header -->
                <div class="flex items-center justify-between">
                    <div>
                        <h1 class="text-2xl font-bold text-gray-900">Edit Data Kehadiran</h1>
                        <p class="mt-1 text-sm text-gray-600">
                            Edit data kehadiran {{ kehadiran.nama_pimpinan }} - {{ formatDate(kehadiran.tanggal) }}
                        </p>
                    </div>
                    <Link :href="route('kehadiran.index')"
                        class="inline-flex items-center px-4 py-2 text-xs font-semibold tracking-widest text-gray-700 uppercase transition duration-150 ease-in-out bg-gray-300 border border-transparent rounded-md hover:bg-gray-400">
                    <i class="mr-2 fas fa-arrow-left"></i>
                    Kembali
                    </Link>
                </div>

                <!-- Form -->
                <div class="bg-white rounded-lg shadow-lg">
                    <form @submit.prevent="submit" class="p-6 space-y-6">
                        <div class="grid grid-cols-1 gap-6 md:grid-cols-2">
                            <!-- Tanggal -->
                            <div>
                                <label for="tanggal" class="block mb-2 text-sm font-medium text-gray-700">
                                    Tanggal <span class="text-red-500">*</span>
                                </label>
                                <input type="date" id="tanggal" v-model="form.tanggal" :class="[
                                    'block w-full rounded-md shadow-sm sm:text-sm',
                                    errors.tanggal
                                        ? 'border-red-300 focus:border-red-500 focus:ring-red-500'
                                        : 'border-gray-300 focus:border-blue-500 focus:ring-blue-500'
                                ]" required>
                                <p v-if="errors.tanggal" class="mt-1 text-sm text-red-600">
                                    {{ errors.tanggal }}
                                </p>
                            </div>

                            <!-- Nama Pimpinan -->
                            <div>
                                <label for="nama_pimpinan" class="block mb-2 text-sm font-medium text-gray-700">
                                    Nama Pimpinan <span class="text-red-500">*</span>
                                </label>
                                <div class="relative">
                                    <!-- Search Input -->
                                    <input type="text" v-model="dosenSearch" @focus="showDosenDropdown = true"
                                        @blur="hideDosenDropdown" placeholder="Cari nama pimpinan..."
                                        class="block w-full px-3 py-2 border rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                                        :class="{
                                            'border-red-300': errors.nama_pimpinan,
                                            'border-gray-300': !errors.nama_pimpinan
                                        }" />

                                    <!-- Dropdown -->
                                    <div v-if="showDosenDropdown && filteredFormasi.length > 0"
                                        class="absolute z-10 w-full mt-1 overflow-y-auto bg-white border border-gray-300 rounded-md shadow-lg max-h-60">
                                        <div v-for="item in filteredFormasi" :key="item.id" @click="selectDosen(item)"
                                            class="px-3 py-2 text-sm border-b border-gray-100 cursor-pointer hover:bg-gray-100 last:border-b-0">
                                            <div class="font-medium text-gray-900">
                                                {{ item.dosen.nama }}
                                            </div>
                                            <div class="text-xs text-gray-500">
                                                {{ item.jabatan.namajabatan }} - {{ item.dosen.fakultas?.name ||
                                                    "Fakultas tidak ditemukan" }}
                                            </div>
                                        </div>
                                    </div>

                                    <!-- No results -->
                                    <div v-if="showDosenDropdown && filteredFormasi.length === 0 && dosenSearch"
                                        class="absolute z-10 w-full mt-1 bg-white border border-gray-300 rounded-md shadow-lg">
                                        <div class="px-3 py-2 text-sm text-gray-500">
                                            Tidak ada pimpinan yang ditemukan
                                        </div>
                                    </div>

                                    <!-- Clear button -->
                                    <button v-if="selectedDosen" type="button" @click="clearDosen"
                                        class="absolute inset-y-0 right-0 flex items-center pr-3">
                                        <svg class="w-4 h-4 text-gray-400 hover:text-gray-600" fill="none"
                                            stroke="currentColor" viewBox="0 0 24 24">
                                            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                                                d="M6 18L18 6M6 6l12 12"></path>
                                        </svg>
                                    </button>
                                </div>

                                <!-- Selected dosen display -->
                                <div v-if="selectedDosen" class="p-2 mt-2 border border-gray-200 rounded-md bg-gray-50">
                                    <div class="text-sm font-medium text-gray-900">
                                        {{ selectedDosen.dosen.nama }}
                                    </div>
                                    <div class="text-xs text-gray-500">
                                        {{ selectedDosen.jabatan.namajabatan }} - {{ selectedDosen.dosen.fakultas?.name
                                            || "Fakultas tidak ditemukan" }}
                                    </div>
                                </div>
                                <p v-if="errors.nama_pimpinan" class="mt-1 text-sm text-red-600">
                                    {{ errors.nama_pimpinan }}
                                </p>
                            </div>

                            <!-- NIP -->
                            <div>
                                <label for="nip" class="block mb-2 text-sm font-medium text-gray-700">
                                    NIP
                                </label>
                                <input type="text" id="nip" v-model="form.nip" placeholder="NIP akan terisi otomatis"
                                    readonly :class="[
                                        'block w-full rounded-md shadow-sm sm:text-sm bg-gray-50',
                                        errors.nip
                                            ? 'border-red-300 focus:border-red-500 focus:ring-red-500'
                                            : 'border-gray-300 focus:border-gray-500 focus:ring-gray-500'
                                    ]">
                                <p v-if="errors.nip" class="mt-1 text-sm text-red-600">
                                    {{ errors.nip }}
                                </p>
                            </div>

                            <!-- Jabatan -->
                            <div>
                                <label for="jabatan" class="block mb-2 text-sm font-medium text-gray-700">
                                    Jabatan <span class="text-red-500">*</span>
                                </label>
                                <input type="text" id="jabatan" v-model="form.jabatan"
                                    placeholder="Jabatan akan terisi otomatis" readonly :class="[
                                        'block w-full rounded-md shadow-sm sm:text-sm bg-gray-50',
                                        errors.jabatan
                                            ? 'border-red-300 focus:border-red-500 focus:ring-red-500'
                                            : 'border-gray-300 focus:border-gray-500 focus:ring-gray-500'
                                    ]" required>
                                <p v-if="errors.jabatan" class="mt-1 text-sm text-red-600">
                                    {{ errors.jabatan }}
                                </p>
                            </div>

                            <!-- Status -->
                            <div>
                                <label for="status" class="block mb-2 text-sm font-medium text-gray-700">
                                    Status Kehadiran <span class="text-red-500">*</span>
                                </label>
                                <select id="status" v-model="form.status" :class="[
                                    'block w-full rounded-md shadow-sm sm:text-sm',
                                    errors.status
                                        ? 'border-red-300 focus:border-red-500 focus:ring-red-500'
                                        : 'border-gray-300 focus:border-blue-500 focus:ring-blue-500'
                                ]" required>
                                    <option value="">Pilih Status</option>
                                    <option value="hadir">Hadir</option>
                                    <option value="tidak_hadir">Tidak Hadir</option>
                                    <option value="terlambat">Terlambat</option>
                                    <option value="sakit">Sakit</option>
                                    <option value="izin">Izin</option>
                                </select>
                                <p v-if="errors.status" class="mt-1 text-sm text-red-600">
                                    {{ errors.status }}
                                </p>
                            </div>

                            <!-- Jam Masuk -->
                            <div>
                                <label for="jam_masuk" class="block mb-2 text-sm font-medium text-gray-700">
                                    Jam Masuk
                                </label>
                                <input type="time" id="jam_masuk" v-model="form.jam_masuk" :class="[
                                    'block w-full rounded-md shadow-sm sm:text-sm',
                                    errors.jam_masuk
                                        ? 'border-red-300 focus:border-red-500 focus:ring-red-500'
                                        : 'border-gray-300 focus:border-blue-500 focus:ring-blue-500'
                                ]">
                                <p v-if="errors.jam_masuk" class="mt-1 text-sm text-red-600">
                                    {{ errors.jam_masuk }}
                                </p>
                                <p class="mt-1 text-xs text-gray-500">
                                    Kosongkan jika status bukan "Hadir" atau "Terlambat"
                                </p>
                            </div>

                            <!-- Jam Keluar -->
                            <div>
                                <label for="jam_keluar" class="block mb-2 text-sm font-medium text-gray-700">
                                    Jam Keluar
                                </label>
                                <input type="time" id="jam_keluar" v-model="form.jam_keluar" :class="[
                                    'block w-full rounded-md shadow-sm sm:text-sm',
                                    errors.jam_keluar
                                        ? 'border-red-300 focus:border-red-500 focus:ring-red-500'
                                        : 'border-gray-300 focus:border-blue-500 focus:ring-blue-500'
                                ]">
                                <p v-if="errors.jam_keluar" class="mt-1 text-sm text-red-600">
                                    {{ errors.jam_keluar }}
                                </p>
                                <p class="mt-1 text-xs text-gray-500">
                                    Opsional - dapat diisi kemudian
                                </p>
                            </div>
                        </div>

                        <!-- Keterangan -->
                        <div>
                            <label for="keterangan" class="block mb-2 text-sm font-medium text-gray-700">
                                Keterangan
                            </label>
                            <textarea id="keterangan" v-model="form.keterangan" rows="4"
                                placeholder="Masukkan keterangan tambahan (opsional)" :class="[
                                    'block w-full rounded-md shadow-sm sm:text-sm',
                                    errors.keterangan
                                        ? 'border-red-300 focus:border-red-500 focus:ring-red-500'
                                        : 'border-gray-300 focus:border-blue-500 focus:ring-blue-500'
                                ]"></textarea>
                            <p v-if="errors.keterangan" class="mt-1 text-sm text-red-600">
                                {{ errors.keterangan }}
                            </p>
                        </div>

                        <!-- Form Actions -->
                        <div class="flex items-center justify-end pt-6 space-x-4 border-t border-gray-200">
                            <Link :href="route('kehadiran.index')"
                                class="inline-flex items-center px-4 py-2 text-xs font-semibold tracking-widest text-gray-700 uppercase transition duration-150 ease-in-out bg-gray-300 border border-transparent rounded-md hover:bg-gray-400">
                            Batal
                            </Link>
                            <button type="submit" :disabled="form.processing" :class="[
                                'inline-flex items-center px-4 py-2 border border-transparent rounded-md font-semibold text-xs text-white uppercase tracking-widest transition ease-in-out duration-150',
                                form.processing
                                    ? 'bg-gray-400 cursor-not-allowed'
                                    : 'bg-blue-600 hover:bg-blue-700'
                            ]">
                                <i v-if="form.processing" class="mr-2 fas fa-spinner fa-spin"></i>
                                <i v-else class="mr-2 fas fa-save"></i>
                                {{ form.processing ? 'Menyimpan...' : 'Update Data' }}
                            </button>
                        </div>
                    </form>
                </div>

                <!-- Audit Log -->
                <div class="p-4 border border-gray-200 rounded-md bg-gray-50">
                    <div class="flex">
                        <div class="flex-shrink-0">
                            <i class="text-gray-400 fas fa-history"></i>
                        </div>
                        <div class="ml-3">
                            <h3 class="text-sm font-medium text-gray-800">
                                Informasi Data
                            </h3>
                            <div class="mt-2 text-sm text-gray-600">
                                <p>Data dibuat: {{ formatDateTime(kehadiran.created_at) }}</p>
                                <p v-if="kehadiran.updated_at !== kehadiran.created_at">
                                    Terakhir diubah: {{ formatDateTime(kehadiran.updated_at) }}
                                </p>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </template>
        <template #footer></template>
    </AdminLayout>
</template>

<script setup>
import { reactive, ref, computed, onMounted } from 'vue'
import { useForm, Link } from '@inertiajs/vue3'
import AdminLayout from '@/Layouts/Container.vue'
import Header from "@/Components/Header.vue";

const props = defineProps({
    kehadiran: Object,
    errors: Object,
    main_menu: {
        type: Array,
        required: true,
    },
    formasi: {
        type: Array,
        required: false,
    }
})

// Searchable dosen select
const dosenSearch = ref("")
const showDosenDropdown = ref(false)
const selectedDosen = ref(null)

// Computed property for filtered formasi
const filteredFormasi = computed(() => {
    // Handle both array and object with data property
    const formasiData = Array.isArray(props.formasi) ? props.formasi : (props.formasi?.data || [])

    if (!dosenSearch.value) {
        return formasiData
    }
    return formasiData.filter((item) =>
        item.dosen?.nama?.toLowerCase().includes(dosenSearch.value.toLowerCase())
    )
})

// Methods for dosen selection
const selectDosen = (item) => {
    selectedDosen.value = item
    form.nama_pimpinan = item.dosen.nama
    form.nip = item.dosen.nip || ''
    form.jabatan = item.jabatan.namajabatan
    dosenSearch.value = item.dosen.nama
    showDosenDropdown.value = false
}

const clearDosen = () => {
    selectedDosen.value = null
    form.nama_pimpinan = ''
    form.nip = ''
    form.jabatan = ''
    dosenSearch.value = ""
    showDosenDropdown.value = false
}

const hideDosenDropdown = () => {
    // Add small delay to allow click events on dropdown items
    setTimeout(() => {
        showDosenDropdown.value = false
    }, 150)
}

// Initialize form with existing data
const form = useForm({
    tanggal: props.kehadiran.tanggal ? new Date(props.kehadiran.tanggal).toISOString().split('T')[0] : '',
    nama_pimpinan: props.kehadiran.nama_pimpinan,
    nip: props.kehadiran.nip || '',
    jabatan: props.kehadiran.jabatan,
    status: props.kehadiran.status,
    jam_masuk: props.kehadiran.jam_masuk || '',
    jam_keluar: props.kehadiran.jam_keluar || '',
    keterangan: props.kehadiran.keterangan || ''
})

// Initialize search with current data
onMounted(() => {
    if (props.kehadiran.nama_pimpinan) {
        dosenSearch.value = props.kehadiran.nama_pimpinan
        // Try to find the corresponding formasi item
        const formasiData = Array.isArray(props.formasi) ? props.formasi : (props.formasi?.data || [])
        const foundItem = formasiData.find(item => item.dosen.nama === props.kehadiran.nama_pimpinan)
        if (foundItem) {
            selectedDosen.value = foundItem
        }
    }
})

const submit = () => {
    form.put(route('kehadiran.update', props.kehadiran.id), {
        onSuccess: () => {
            // Success handled by redirect
        }
    })
}

const formatDate = (dateString) => {
    if (!dateString) return '-'
    const date = new Date(dateString)
    return date.toLocaleDateString('id-ID', {
        year: 'numeric',
        month: 'long',
        day: 'numeric'
    })
}

const formatDateTime = (dateTimeString) => {
    if (!dateTimeString) return '-'
    const date = new Date(dateTimeString)
    return date.toLocaleString('id-ID', {
        year: 'numeric',
        month: 'long',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
    })
}
</script>